import React from 'react'
import { Edit2, Copy, Trash2, Shield, Users } from 'lucide-react'
import { Modal, Button } from '@components/common'
import './Roles.css'

/**
 * RoleView Component
 *
 * Modal for viewing role details
 *
 * @param {Object} props
 * @param {Object} props.roleData - Role data to display
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onClone - Clone handler
 * @param {Function} props.onDelete - Delete handler
 */
const RoleView = ({ roleData, onClose, onEdit, onClone, onDelete }) => {
	if (!roleData) return null

	const isSystemRole = roleData.is_system_default === true || roleData.is_system_default === 1 || roleData.is_system_default === '1'
	const isArchived = roleData.status === 'archived' || roleData.deleted_at

	// Parse permissions - handle both string and object formats
	let permissions = {}
	try {
		if (typeof roleData.permissions === 'string') {
			// Try to parse JSON string
			permissions = JSON.parse(roleData.permissions || '{}')
		} else if (typeof roleData.permissions === 'object' && roleData.permissions !== null) {
			permissions = roleData.permissions
		}
	} catch (error) {
		console.error('Failed to parse permissions:', error)
		permissions = {}
	}

	// Additional check: ensure permissions is an object, not a string
	if (typeof permissions === 'string') {
		// Double-encoded JSON - parse again
		try {
			permissions = JSON.parse(permissions)
		} catch (error) {
			console.error('Failed to parse double-encoded permissions:', error)
			permissions = {}
		}
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = () => {
		if (isArchived) {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	/**
	 * Get type badge
	 */
	const getTypeBadge = () => {
		if (isSystemRole) {
			return <span className="badge badge--primary">System Default</span>
		}
		return <span className="badge badge--info">Custom Role</span>
	}

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			size="large"
			closeOnBackdrop={false}
			title={
				<div className="role-view__header-content">
					<div>
						<div className="role-view__title">{roleData.name}</div>
						<div className="role-view__badges">
							{getTypeBadge()}
							{getStatusBadge()}
						</div>
					</div>
				</div>
			}
			footer={
				<>
					<Button variant="secondary" onClick={onClose}>
						Close
					</Button>
					<div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
						<Button variant="info" onClick={onClone} icon={<Copy size={16} />}>
							Clone
						</Button>
						{!isArchived && (
							<Button variant="primary" onClick={onEdit} icon={<Edit2 size={16} />}>
								Edit
							</Button>
						)}
						{!isSystemRole && !isArchived && (
							<Button variant="danger" onClick={onDelete} icon={<Trash2 size={16} />}>
								Delete
							</Button>
						)}
					</div>
				</>
			}
		>
			<div className="role-view">
				{/* Basic Information */}
				<div className="role-view__section">
					<h3 className="role-view__section-title">
						<Shield size={20} />
						Role Information
					</h3>
					<div className="role-view__grid">
						<div className="role-view__field">
							<label>Role Name</label>
							<p>{roleData.name}</p>
						</div>
						<div className="role-view__field">
							<label>Slug</label>
							<p className="role-view__slug">{roleData.slug}</p>
						</div>
						{roleData.description && (
							<div className="role-view__field role-view__field--full">
								<label>Description</label>
								<p>{roleData.description}</p>
							</div>
						)}
					</div>
				</div>

				{/* Permissions */}
				{permissions && typeof permissions === 'object' && Object.keys(permissions).length > 0 && (
					<div className="role-view__section">
						<h3 className="role-view__section-title">
							<Users size={20} />
							Permissions ({Object.keys(permissions).length} {Object.keys(permissions).length === 1 ? 'resource' : 'resources'})
						</h3>
						<div className="role-view__permissions">
							{/* Check for wildcard/full access permissions */}
							{permissions['*'] && Array.isArray(permissions['*']) && permissions['*'].includes('*') ? (
								<div className="role-view__full-access">
									<span className="badge badge--success">Full Access</span>
									<p>This role has unrestricted access to all resources and actions across the entire system.</p>
								</div>
							) : (
								Object.entries(permissions).map(([resource, actions]) => (
									<div key={resource} className="role-view__permission-group">
										<h4 className="role-view__permission-resource">{resource}</h4>
										<div className="role-view__permission-actions">
											{Array.isArray(actions) ? (
												actions.map((action, index) => (
													<span key={`${resource}-${action}-${index}`} className="badge badge--secondary">
														{action}
													</span>
												))
											) : (
												<span className="badge badge--secondary">{String(actions)}</span>
											)}
										</div>
									</div>
								))
							)}
						</div>
					</div>
				)}

				{/* Metadata */}
				<div className="role-view__section">
					<h3 className="role-view__section-title">Metadata</h3>
					<div className="role-view__grid">
						{roleData.created_at && (
							<div className="role-view__field">
								<label>Created</label>
								<p>{new Date(roleData.created_at).toLocaleDateString()}</p>
							</div>
						)}
						{roleData.updated_at && (
							<div className="role-view__field">
								<label>Last Updated</label>
								<p>{new Date(roleData.updated_at).toLocaleDateString()}</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}

export default RoleView
